import { Component } from '@angular/core';
import { menu } from '@app/core/constants/menu';
import { MenuElement } from '@shared/interfaces/header/menu-element';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { TranslatePipe } from '@ngx-translate/core';
import { CollapseService } from '@app/core/services/collapse.service';
import { UserService } from '@app/core/services/user.service';
import { TooltipDirective } from '@shared/directives/tooltip/tooltip.directive';

/**
 * The NavigationMenuComponent is an Angular component responsible for rendering a navigation menu.
 * It provides support for displaying menu items with nested structures and integrates with
 * routing and internationalization functionalities.
 *
 * Features:
 * - Dynamically displays menu items with labels.
 * - Supports nested menu structures through `MenuElement` items.
 * - Utilizes localization and translation features via the TranslatePipe.
 * - Handles the collapsible state of the menu through CollapseService.
 */
@Component({
  selector: 'app-navigation-menu',
  imports: [
    RouterLink,
    LucideAngularModule,
    RouterLinkActive,
    TranslatePipe,
    TooltipDirective,
  ],
  templateUrl: './navigation-menu.component.html',
  styleUrl: './navigation-menu.component.css'
})

export class NavigationMenuComponent {
  public menuItems: { label: string, items: MenuElement[] }[] = menu;

  constructor(
    public readonly collapseService: CollapseService,
    private readonly userService: UserService,
  ) { }

  /** Returns only the items visible to the current user, filtering by role once. */
  public visibleItems(items: MenuElement[]): MenuElement[] {
    const userRoles: string[] = this.userService.user()?.roles ?? [];
    return items.filter(item => {
      if (!item.roles) return true;
      const required = Array.isArray(item.roles) ? item.roles : [item.roles];
      return required.length === 0 || required.some(r => userRoles.includes(r));
    });
  }
}
